import { z } from 'zod';
import { type AllVizChartConfig } from '../visualizations/types';
import { type AudiencePlannerResponse } from './aiAudience';
import { type AiEntityList } from './aiEntities';
import { type AnyType } from './any';
import { type CommunicationChannel } from './communications';
import {
    type CustomAttributeType,
    type InsertCustomAttribute,
} from './customAttributes';
import { type UpdateCustomMaterialisation } from './customMaterialisations';
import {
    MetricType,
    type CustomDimension,
    type DimensionType,
    type Schedule,
    type TableCalculation,
} from './field';
import { FilterOperator, UnitOfTime } from './filter';
import { type LearningBlockMapping } from './intelligenceBlock';
import { type JourneyCreatePayload } from './journeys';
import {
    type AdditionalMetric,
    type MetricQuery,
    type NestedMetricQuery,
    type NestedMetricQueryGroup,
} from './metricQuery';
import { type JoinType, type RelationTableType } from './relation/enums';
import type {
    CachedColumnValues,
    CompiledRelation,
    CompiledRelationJoin,
} from './relation/relations';
import { type ChartConfig } from './savedCharts';
import { type TemplateContentDetails } from './templates';
import { type Timezones } from './timezone';

export const USER_THREAD_MAPPING_TABLE_NAME = 'user_thread_mapping';
export const USER_MESSAGE_FEEDBACK_TABLE_NAME = 'user_message_feedback';
export const LANGSMITH_USER_MESSAGE_FEEDBACK_KEY = 'user_message_feedback';

// The use case context in which AI is receiving and generating messages
export enum AIMessageContext {
    SQL_AUDIENCE = 'sql_audience',
    VISUAL_AUDIENCE = 'visual_audience',
    HOME_PAGE = 'home_page',
    EVENT_SETUP = 'events',
    OTHERS = 'others',
    CREATE_CAMPAIGN = 'create_campaign',
    DESIGN_TEMPLATE = 'design_template',
    BUILD_JOURNEY = 'build_journey',
    SET_UP_TASKS = 'set_up_tasks',
    SET_UP_PROJECTS = 'set_up_projects',
    SET_UP_EXPERIMENT = 'set_up_experiment',
    SCHEMA_SEARCH = 'schema_search',
    CHART = 'chart',
    CUSTOM_ATTRIBUTE = 'custom_attribute',
    CUSTOM_ATTRIBUTE_CREATION = 'custom_attribute_creation',
    KPI_MANAGER = 'kpi_manager',
    RELATIONS = 'relations',
    INTERRUPT = 'interrupt',
    ANALYTICS = 'analytics',
}

export enum OpenCodeAgentName {
    SORTMENT_AGENT = 'sortment-agent',
    QUEST_LAUNCHER = 'quest-launcher',
    AUDIENCE_BUILDER = 'audience-builder',
    CUSTOM_ATTRIBUTE = 'custom-attribute',
    ANALYTICS = 'analytics',
    CAMPAIGN_CREATOR = 'campaign-creator',
    BEEFREE_V2_TEMPLATE_CREATOR = 'beefree-v2-template-creator',
    MJML_TEMPLATE_CREATOR = 'mjml-template-creator',
    JOURNEY_CREATOR = 'journey-creator',
    BACKGROUND_TASK_MANAGER = 'background-task-manager',
    KPI_MANAGER = 'kpi-manager',
}

export const AI_MESSAGE_CONTEXT_OPENCODE_AGENT: Partial<
    Record<AIMessageContext, OpenCodeAgentName>
> = {
    [AIMessageContext.SQL_AUDIENCE]: OpenCodeAgentName.AUDIENCE_BUILDER,
    [AIMessageContext.VISUAL_AUDIENCE]: OpenCodeAgentName.AUDIENCE_BUILDER,
    [AIMessageContext.CUSTOM_ATTRIBUTE]: OpenCodeAgentName.CUSTOM_ATTRIBUTE,
    [AIMessageContext.CUSTOM_ATTRIBUTE_CREATION]:
        OpenCodeAgentName.CUSTOM_ATTRIBUTE,
    [AIMessageContext.KPI_MANAGER]: OpenCodeAgentName.KPI_MANAGER,
    [AIMessageContext.ANALYTICS]: OpenCodeAgentName.ANALYTICS,
    [AIMessageContext.CHART]: OpenCodeAgentName.ANALYTICS,
    [AIMessageContext.CREATE_CAMPAIGN]: OpenCodeAgentName.CAMPAIGN_CREATOR,
    [AIMessageContext.DESIGN_TEMPLATE]: OpenCodeAgentName.MJML_TEMPLATE_CREATOR,
    [AIMessageContext.BUILD_JOURNEY]: OpenCodeAgentName.JOURNEY_CREATOR,
    [AIMessageContext.SET_UP_TASKS]: OpenCodeAgentName.BACKGROUND_TASK_MANAGER,
    [AIMessageContext.SET_UP_PROJECTS]: OpenCodeAgentName.QUEST_LAUNCHER,
};

export const getOpenCodeAgentForMessageContext = (
    context?: AIMessageContext | null,
    message?: string,
): OpenCodeAgentName => {
    if (!context) {
        return OpenCodeAgentName.SORTMENT_AGENT;
    }

    if (context === AIMessageContext.DESIGN_TEMPLATE && message) {
        const lowerMessage = message.toLowerCase();
        const isMjml =
            lowerMessage.includes('<mjmltemplate') ||
            lowerMessage.includes('<mjmlcontext') ||
            /templateid[=:]/i.test(message);
        if (isMjml) {
            return OpenCodeAgentName.MJML_TEMPLATE_CREATOR;
        }
    }

    return (
        AI_MESSAGE_CONTEXT_OPENCODE_AGENT[context] ??
        OpenCodeAgentName.SORTMENT_AGENT
    );
};

export type AIContent =
    | TextContent
    | ImageContent
    | AgentJsonContent
    | TableJsonContent
    | AgentErrorContent
    | MemoryContent
    | FeedbackContent;

export type AgentMessage = {
    threadId: string;
    messageId: string;
    author: Author;
    context: AIMessageContext;
    content: AIContent;
    additional_kwargs?: AnyType;
    traceId: string;
    mentionedEntities?: AiEntityList[];
};

export type RelationDetails = {
    relationId: string;
    relation: CompiledRelation;
};

export enum AiMessageTypes {
    TEXT = 'text',
    IMAGE = 'image',
    JSON = 'json',
    TABLE_JSON = 'table_json',
    CHART = 'chart',
    ERROR = 'error',
    MEMORY_EXTRACTED = 'memory_extracted',
    MEMORY_REFERENCED = 'memory_referenced',
    FEEDBACK = 'feedback',
}

export const feedbackRequiredMessageTypes = [
    AiMessageTypes.JSON,
    AiMessageTypes.TABLE_JSON,
];

export type ToolResponse = {
    json?: TableJsonContent | AIChartContent;
    content: string;
};

export type AgentJsonContent = {
    type: AiMessageTypes.JSON;
    value: SQLAudience | VisualAudience | Chart | CustomAttributeAgent;
};

export type TableJsonContent = {
    type: AiMessageTypes.TABLE_JSON;
    value: TableJson[];
};

export type AIChartContent = {
    type: AiMessageTypes.CHART;
    value: Chart;
};

export type AgentErrorContent = {
    type: AiMessageTypes.ERROR;
    value: string;
};

export type TextContent = {
    type: AiMessageTypes.TEXT;
    value: string;
    thinkingText?: string;
};

type ImageContent = {
    type: AiMessageTypes.IMAGE;
    value: string;
    alt?: string;
};

export type ReferencedMemoryWithTitle = {
    content: string;
    timestamp: Date;
    blockId?: string;
    blockTitle?: string;
};

export type MemoryExtractedContent = {
    type: AiMessageTypes.MEMORY_EXTRACTED;
    value: LearningBlockMapping[];
};

export type MemoryReferencedContent = {
    type: AiMessageTypes.MEMORY_REFERENCED;
    value: ReferencedMemoryWithTitle[];
};

export type FeedbackContent = {
    type: AiMessageTypes.FEEDBACK;
    value: string;
};

export type MemoryContent = MemoryExtractedContent | MemoryReferencedContent;

export type VisualAudience = {
    context: AIMessageContext.VISUAL_AUDIENCE;
    nestedMetricQuery: NestedMetricQuery;
};

export type SQLAudience = {
    context: AIMessageContext.SQL_AUDIENCE;
    sql: string;
    explaination: string;
};

export type TableJson = {
    fields: Record<string, AnyType>;
    rows: AnyType[];
    sql?: string;
    title?: string;
};

export type Chart = {
    context: AIMessageContext.CHART;
    metricQuery: MetricQuery;
    chartConfig: ChartConfig;
};

export type CustomAttributeAgent = {
    context:
        | AIMessageContext.CUSTOM_ATTRIBUTE
        | AIMessageContext.CUSTOM_ATTRIBUTE_CREATION;
    customAttribute: InsertCustomAttribute;
};

export type NaturalLanguageSQLResult = {
    question: string;
    response: string | null;
    summary?: string;
};

export enum Author {
    USER = 'user',
    SYSTEM = 'system',
}

export type CsvData = {
    csvOpenAIFileId: string;
    columnNames: string[];
    rowCount: number;
    csvGeneratedFor: string;
};

export type OpenAIObjectIds = {
    assistantId: string;
};

export type FormattedColumn = {
    name: string;
    type: DimensionType;
    description?: string;
    cachedValues?: CachedColumnValues[];
    aiContext?: string;
    synonyms?: string;
};

export type FormattedCustomAttribute = {
    id: string;
    name: string;
    description: string | null;
    sql: string;
};

export type FormattedTable = {
    tableName: string;
    tableLabel: string;
    description: string;
    primaryKey: string;
    type: RelationTableType;
    columns: FormattedColumn[];
    database: string;
    schema: string;
    eventNameColumn?: string;
    eventTimestampColumn?: string;
};

export type FormattedCampaign = {
    id?: string;
    name?: string;
    description?: string;
};

export type FormattedJourney = {
    id: string;
    name: string;
    description?: string;
};

export type FormattedMediaLibraryAsset = {
    id: string;
    name: string;
    description?: string;
    mimeType: string;
    publicUrl?: string;
    labels: string[];
    dimensions?: { width: number; height: number } | null;
    sizeBytes: number;
};

export type RelevantSchemaInfo = {
    tableColumns: FormattedTable[];
    customDimensions: Array<FormattedCustomAttribute>;
    customMetrics: Array<FormattedCustomAttribute>;
    customSqlTraits: Array<FormattedCustomAttribute>;
    joins: CompiledRelationJoin[];
    audiences: AudienceWithSql[];
    campaigns: FormattedCampaign[];
    journeys: FormattedJourney[];
    mediaLibraryAssets: FormattedMediaLibraryAsset[];
};

export type AgentContext = {
    messageContext: AIMessageContext;
    schemaLinks?: RelevantSchemaInfo;
    projectUuid?: string;
    organisationUuid?: string;
    userUuid?: string;
    customAttribute?: AdditionalMetric | CustomDimension | TableCalculation;
    cookie?: string;
    audiencePlan?: AudiencePlannerResponse;
    csvDataList: CsvData[];
    openAIObjectIds?: OpenAIObjectIds;
    generatedCharts?: GeneratedChart[];
    retryCount: number;
    sqlResult?: {
        sql: string;
        explanation: string;
    };
    metricQuery?: NestedMetricQueryGroup;
    compiledQuery?: string;
    memories?: FormattedMemory[];
    suggestedMemories?: FormattedMemory[];
    currentDate?: string;
    workspaceTimezone?: Timezones;
    customAttributeSql?: string;
    insertCustomAttribute?: InsertCustomAttribute;
    fallbackInsertCustomAttribute?: InsertCustomAttribute;
    customAttributeType?: CustomAttributeType;
    entitySearchQueries?: string[];
    conversationSummary?: ConversationSummary | null;
};

export type FormattedMemory = {
    content: string;
    timestamp: Date;
};

export type GeneratedChart = {
    chartUrl: string;
    timestamp: string;
};

export interface Column {
    name: string;
    type: string;
    description?: string;
    label?: string;
}

export type TableDescription = {
    tableName: string;
    tableDescription: string;
    tableLabel: string;
    runId: string;
};

export type EventColumnMapping = {
    key: string;
    columnName: string;
    columnDataType: string;
    label: string;
    nullable: boolean;
};

export type ColumnDetails = {
    name: string;
    description: string;
    label: string;
    masked?: boolean;
    cached?: boolean;
};

export type RelationsServiceRequest = {
    type: string;
    relationID: string;
    tableID: string;
};

export type TableDetails = {
    type: RelationTableType;
    relationID: string;
    tableID: string;
};

export type EventReservedKeysRequest = {
    user_id: string;
    event_id: string;
    timestamp: string;
};

export type EventExistingColumnsRequest = {
    existingColumns: Record<string, string>;
};

/**
 * Hard ceiling on nodes named per request. Over this → HTTP 400 (no silent slice).
 */
export const MAX_JOURNEY_NODE_NAMING_NODES = 25;

/** Few-shot node names mined from the workspace's own journeys. */
export const MAX_WORKSPACE_NODE_NAME_EXAMPLES = 15;

/** Per-string caps for model-facing journey/node naming prompts. */
export const MAX_NAMING_SIGNAL_CHARS = 120;
export const MAX_NAMING_CRITERION_VALUE_CHARS = 80;
export const MAX_NAMING_USER_TEXT_CHARS = 120;
export const MAX_NAMING_WORKSPACE_EXAMPLE_CHARS = 50;
export const MAX_NAMING_PAST_JOURNEY_EXAMPLE_CHARS = 120;
export const MAX_NAMING_PROMPT_JSON_CHARS = 12_000;
export const MAX_JOURNEY_NAMING_ENTITY_REFS = 50;
export const MAX_JOURNEY_NAMING_NODE_SIGNALS = 30;
export const MAX_NAMING_CRITERIA_PER_NODE = 8;
export const MAX_NAMING_SIBLING_NODE_NAMES = 10;

/** Default server work budget for journey/node naming (matches model timeout). */
export const DEFAULT_JOURNEY_NAMING_SERVER_DEADLINE_MS = 60_000;
export const MIN_JOURNEY_NAMING_SERVER_DEADLINE_MS = 1_000;
export const MAX_JOURNEY_NAMING_SERVER_DEADLINE_MS = 60_000;

export const clampJourneyNamingServerDeadlineMs = (
    deadlineMs?: number,
): number => {
    const value = deadlineMs ?? DEFAULT_JOURNEY_NAMING_SERVER_DEADLINE_MS;
    return Math.min(
        MAX_JOURNEY_NAMING_SERVER_DEADLINE_MS,
        Math.max(MIN_JOURNEY_NAMING_SERVER_DEADLINE_MS, value),
    );
};

export type JourneyNodeNamingTarget =
    | 'new'
    | { journeyId: string; versionId: string };

export type JourneyNodeNameDescriptionRequest = {
    /** Authoritative create vs update scope for authz. */
    target: JourneyNodeNamingTarget;
    journey: JourneyCreatePayload;
    nodeIds: string[];
    /** Remaining server work budget in ms; capped server-side. */
    deadlineMs?: number;
};

export type JourneyNodeNamingOutcome =
    | 'applied'
    | 'skipped'
    | 'rejected'
    | 'error';

export type JourneyNodeNamingPatch = {
    nodeId: string;
    title: string;
    description: string;
};

export type GenerateAiClassifyPromptRequest = {
    seedPrompt: string;
    refinementPrompt?: string;
    previousPolishedPrompt?: string;
    previousClassifications?: string[];
};

export type GenerateAiClassifyPromptResponse = {
    polishedPrompt: string;
    classifications: string[];
};

export type JourneyNodeNameDescription = {
    nodeId: string;
    nodeName: string;
    nodeDescription: string;
    /** Present on API responses after eligibility/normalize. */
    outcome?: JourneyNodeNamingOutcome;
};

export type JourneyNodeNameDescriptionResponse = {
    nodes: JourneyNodeNameDescription[];
};

export type SchemaLinkElement = {
    id: string;
    description: string;
    type?: string;
    primaryKey?: string;
};

export type SchemaLinkJoin = {
    join: string;
    joinType: JoinType;
};

export type AdditionaMetricDetails = {
    name: string;
    explanation: string;
    additionalMetric: z.infer<typeof additionalMetricType>[];
};

export type SchemaLinks = {
    columns: Array<SchemaLinkElement>;
    values: Record<string, Array<string>>;
    foreignKeys: Array<SchemaLinkJoin>;
    audiences: Array<SchemaLinkElement>;
    customMetrics: Array<SchemaLinkElement>;
    explanation: string;
};

export type EntityNameDescription = {
    name: string;
    description: string;
    runId: string;
};

export type VisualAudienceDescriptionRequest = {
    context: AIMessageContext.VISUAL_AUDIENCE;
    nestedMetricQuery: NestedMetricQueryGroup;
};

export type SqlAudienceDescriptionRequest = {
    context: AIMessageContext.SQL_AUDIENCE;
    sqlQuery: string;
};

export type CampaignNameDescriptionRequest = {
    audienceIdArray: string[];
    templateId: string;
    channel: CommunicationChannel;
    schedule: Schedule;
};

export type JourneyNameDescriptionRequest = {
    /** Authoritative create vs update scope for authz (same contract as node naming). */
    target: JourneyNodeNamingTarget;
    journey: JourneyCreatePayload;
    /** Remaining server work budget in ms; capped server-side. */
    deadlineMs?: number;
};

export type TemplateNameDescriptionRequest = {
    templateContent: TemplateContentDetails;
    channel: CommunicationChannel;
};

export type SqlChartNameDescriptionRequest = {
    sql: string;
    limit: number;
    config: AllVizChartConfig;
};

export type AudienceDescriptionRequest =
    | VisualAudienceDescriptionRequest
    | SqlAudienceDescriptionRequest;

export type RelationDetailswithCachedValues = {
    relationId: string;
    relation: CompiledRelation;
    allTableCachedValues: Record<string, Record<string, CachedColumnValues[]>>;
};

export type SuggestedPrompt = {
    prompt: string;
    example: string;
};

export type ProfileSuggestions = {
    CustomMaterialisation: UpdateCustomMaterialisation;
    columnsArray: string[];
};

export type UserThreadMapping = {
    threadId: string;
    userUuid: string;
    projectUuid: string;
    createdAt: Date;
    threadName: string;
    updatedAt: Date;
};

export type UserThreadList = {
    data: UserThreadMapping[] | null;
    paginate: {
        total?: number;
        lastPage?: number;
        currentPage: number;
        perPage: number;
        from: number;
        to: number;
    };
};

export type UserMessageFeedback = {
    threadId: string;
    messageId: string;
    sentiment: boolean;
    metadata: Record<string, string>;
    userUuid: string;
    projectUuid: string;
    createdAt: Date;
    traceId: string;
};

export type AIGeneratedNameDescription = {
    name: string;
    description: string;
    runId: string;
};

export type FinalNameDescription = {
    name?: string;
    description?: string;
};

export type DbUserMessageFeedback = {
    message_id: string;
    thread_id: string;
    user_uuid: string;
    project_uuid: string;
    sentiment: boolean;
    metadata: Record<string, string>;
    created_at: Date;
    trace_id: string;
};

export enum FeedbackAuthor {
    AI = 'ai',
    USER = 'user',
}

export enum FeedbackCategory {
    INCOMPLETE_ANSWER = 'incomplete_answer',
    REFERENCED_WRONG_TABLES = 'referenced_wrong_tables',
    FORMATTING_ISSUES = 'formatting_issues',
    INCORRECT_RESULTS = 'incorrect_results',
    SLOW_RESPONSE = 'slow_response',
    UNCLEAR_EXPLANATION = 'unclear_explanation',
    MISSING_CONTEXT = 'missing_context',
    TECHNICAL_ERRORS = 'technical_errors',
    OTHER = 'other',
}

export type GetUserFeedbackResponseDto = {
    status: string;
    results: UserMessageFeedback;
};

export type ConversationSummary = {
    summary: string;
    countMessagesSummarized: number;
};

export const DEFAULT_AUDIENCE_NAME_FORMAT_GUIDELINES = `
    Format: {KeyBehavior} {UserType} {SegmentCharacteristic} {LifecycleStage}
    - Parameters are optional, so omit any that aren't relevant.
    - Keep names concise (under 50 characters), abbreviate if needed.
    - Max 50 characters
    - Use sentence case, not title case (e.g., "Engaged premium onboarding" not "Engaged Premium Onboarding")

    Examples:
    - Engaged premium onboarding
    - Lapsed basic winback
    - Active new users high value retention
    - Engaged enterprise onboardings
    - Active budget
`;

export const DEFAULT_TEMPLATE_NAME_FORMAT_GUIDELINES = `
  Format: {Channel} {Purpose} {KeyTheme} {Action}
  
  - Parameters are optional, so omit any that aren't relevant to your campaign.
  - Keep names concise (under 50 characters), abbreviate if needed.
  - Max 50 characters.
  - Use sentence case, not title case (e.g., "Email retargeting conversion promo" not "Email Retargeting Conversion Promo")

  Examples:
  - Email retargeting conversion promo
  - Push awareness product launch
  - SMS retention brand loyalty reminder
  - InApp engagement upsell
`;

export const DEFAULT_CAMPAIGN_NAME_FORMAT_GUIDELINES = `
  Format: {Channel} {Purpose} {KeyTheme} {Objective}

  - Parameters are optional, so omit any that aren't relevant.
  - Keep names concise (under 50 characters), abbreviate if needed.
  - Max 50 characters.
  - Use sentence case, not title case (e.g., "Email retargeting holiday discount" not "Email Retargeting Holiday Discount")

  Examples:
  - Email retargeting holiday discount
  - Push awareness brand launch golive
  - SMS retention flash sale conversion
  - Social engagement trend campaign upsell
`;

export const DEFAULT_JOURNEY_NAME_FORMAT_GUIDELINES = `
  Write a short campaign-style name a marketer would save — not a config summary.

  - Keep names concise (under 50 characters), abbreviate if needed.
  - Use sentence case, not title case (e.g., "Ghosting re-engagement drip" not "Ghosting Re-engagement Drip")
  - Reflect the audience/segment and journey goal when available; program or nurture names are fine when that is the purpose.
  - Do not invent an entry trigger when journeyEntrySummary is generic ("Journey entry trigger") or the flow is only wait → send.
  - Avoid mechanical endings like "email send", "triggered send", or "X-triggered Y".
  - Do not center the name or description on absolute wait dates / "scheduled until …" unless waiting is the journey's purpose.

  Examples illustrate variety only — invent a fresh name for this journey:
  - Ghosting re-engagement drip
  - Invoice lifecycle billing reminders
  - MQL advanced nurture re-engage
  - Onboarding incomplete nudge
  - Abandoned booking recovery
  - Daily unsubscribe invalid emails
  - Abandon cart recovery
`;

export const DEFAULT_JOURNEY_NODE_NAME_FORMAT_GUIDELINES = `
  Write a short marketer-typed name inspired by this node's primarySignal.
  Prefer a light paraphrase when it improves scannability; when primarySignal is
  already a short usable phrase (or a wait date), light cleanup is fine.
  Always return a non-empty name and description — never leave either blank.
  supportingSignals and criteria inform the description, not a concatenated title.

  Principles:
  - One idea per name. Vary phrasing to match the signal — do not force a fixed "{Purpose} filter/email" template.
  - Never concatenate fields (no "Platform lo & Ivfbf3cj", no "A and B filter").
  - Never include raw values, partial strings, random ids, snake_case, UUIDs, event sources, or hosts.
  - Calendar dates/times in primarySignal (or supportingSignals) are already in the workspace timezone — use them as written when naming a wait-until-date node (e.g. "Wait until 21 Aug 2025"). Do not invent other dates or reinterpret as UTC.
  - Optional "Kind: purpose" colon style (Wait: Until …, Filter: …) is allowed when it clarifies; not required.
  - For send-message: do not invent "Send: {exact primarySignal}". Prefer a short purpose or cleaned theme without a "Send:" prefix.
  - Prefer business milestones, sequence numbers, or purpose over generic channel labels.
  - Under 50 characters, sentence case.

  Shapes per node kind — these show FORM, not vocabulary. Draw from
  this node's own primarySignal, never from these lines:
  - Wait / Delay: duration, hold, or scheduled date from primarySignal ("Wait 7 days", "Hold for send window", "Wait until 21 Aug 2025")
  - Wait until: the awaited event only ("Wait for onboarding completion") — never event source or person names
  - Filter: the check being made ("Filter: Unpaid at D-7", "Belongs to winback cohort", "Is email invalid?")
  - Split: the dimension it branches on ("Split by purchase history")
  - Send message: purpose, milestone, or sequence ("D-7 reminder", "Welcome email 1", "Abandon cart touch 1")
  - Call API: the purpose of the call ("Trigger email validation") — not POST host/path
  - Update trait / Set: what is recorded ("Mark nurture status", "Set last contacted")
  - Experiment: the experiment name only
  - AI Classify: the intent theme ("Classify support intent")
  - Add to journey: the destination name, or "Add to 2 journeys"
`;

export const additionalMetricType = z.object({
    uuid: z.string(),
    baseDimensionName: z.string(),
    table: z.string(),
    sql: z.string(),
    type: z.nativeEnum(MetricType),
    filters: z
        .array(
            z.object({
                id: z.string(),
                operator: z.nativeEnum(FilterOperator),
                values: z.array(z.string()),
                target: z.object({
                    fieldRef: z.string(),
                }),
                settings: z
                    .object({
                        completed: z.boolean(),
                        unitOfTime: z.nativeEnum(UnitOfTime),
                    })
                    .optional(),
            }),
        )
        .optional(),
    label: z.string(),
    name: z.string(),
    description: z.string(),
});

export type AudienceWithSql = {
    id: string;
    name: string;
    description: string;
    sqlQuery: string;
};

export const DEFAULT_THREAD_NAME = 'New chat';

export type ExtractedMemory = {
    memoryContent: string;
    userFeedback: boolean; // true for positve
};
